import React, { Fragment, useState, useRef, memo } from 'react';
import PropTypes from 'prop-types';

import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import Metrics from '@spotahome/soyuz-tracking';

import Rating from '../../Rating';
import Favourite from '../../Favourite';

import ImageCarousel from '../../ImageCarousel';

import Trash from './Trash';

import HomecardLabelList from './HomecardLabelList';
import HomecardImagePlaceholder from './HomecardImagePlaceholder';
import AnimatedHeart from './AnimatedHeart';

import styles from './HomecardImage.module.scss';

const onImageChangeDefault = eventType => {
  Metrics.ga.sendEvent(
    'Search',
    `homecard slider ${eventType} chevron`,
    'click button'
  );
};

const IMAGES_TO_PRELOAD = 2;

const SingleImage = ({
  hasLazyLoad,
  inView,
  renderRating,
  onLoad,
  image,
  imageSet,
  alt
}) => {
  const [imageLoaded, setImageLoaded] = useState(!hasLazyLoad);

  const handleImageLoad = () => {
    setImageLoaded(true);
    onLoad();
  };

  const homecardImageClasses = classNames(styles['home-card__image'], {
    [styles['home-card__image--hidden']]: !imageLoaded
  });

  const dataTest =
    hasLazyLoad && inView ? 'lazy-homecard-image' : 'homecard-image';

  return (
    <Fragment>
      <div className={styles['home-card__image-mask']} />
      {renderRating()}
      <picture>
        <img
          onLoad={handleImageLoad}
          srcSet={imageSet}
          className={homecardImageClasses}
          alt={alt}
          src={image}
          data-test={dataTest}
        />
      </picture>
      {!imageLoaded && <HomecardImagePlaceholder />}
    </Fragment>
  );
};

SingleImage.propTypes = {
  hasLazyLoad: PropTypes.bool.isRequired,
  inView: PropTypes.bool.isRequired,
  renderRating: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  image: PropTypes.string.isRequired,
  imageSet: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired
};

const HomecardImage = memo(
  ({
    favourite,
    handleFavourite,
    isAnimatedFavorite,
    onDelete,
    image,
    imageSet,
    renderLabels,
    title,
    photos,
    hasLazyLoad,
    onLoad,
    rating,
    onImageChange,
    lazyCarousel,
    isShowStatus
  }) => {
    const [swipedImage, setSwipedImage] = useState(false);

    const animatedHeartRef = useRef();

    const [homecardRef, inView] = useInView({
      triggerOnce: true,
      threshold: 0.25
    });

    const handleImageChange = (...args) => {
      setSwipedImage(true);
      onImageChange(...args);
    };

    const handleHeartAnimation = () => {
      animatedHeartRef.current.classList.toggle(
        styles['home-card__animated-heart--pulsing']
      );
      setTimeout(() => {
        animatedHeartRef.current.classList.toggle(
          styles['home-card__animated-heart--pulsing']
        );
      }, [1000]);
    };

    const handleFavouriteClick = () => {
      if (isAnimatedFavorite && !favourite) {
        handleHeartAnimation();
        return handleFavourite();
      }
      return handleFavourite();
    };

    const renderRating = () =>
      rating ? (
        <div className={styles['home-card__rating-container']}>
          <Rating rating={rating} />
        </div>
      ) : null;

    const renderImageContent = () => (
      <Fragment>
        {photos && photos.length > 1 ? (
          <ImageCarousel
            onImageChange={handleImageChange}
            items={photos}
            ratio={ImageCarousel.RATIOS.HOMECARD}
            preloadRange={swipedImage ? IMAGES_TO_PRELOAD : 0}
            lazyLoad={lazyCarousel}
            keepHeightWhileLoading={!hasLazyLoad}
            withBackgroundChevrons
            isShowStatus={isShowStatus}
          >
            {renderRating()}
          </ImageCarousel>
        ) : (
          <SingleImage
            hasLazyLoad={hasLazyLoad}
            inView={inView}
            renderRating={renderRating}
            onLoad={onLoad}
            image={image}
            imageSet={imageSet}
            alt={title}
          />
        )}
        {isAnimatedFavorite && (
          <AnimatedHeart
            innerRef={animatedHeartRef}
            className={styles['home-card__animated-heart']}
          />
        )}
      </Fragment>
    );

    const favoriteContainerClasses = classNames(
      styles['home-card__favourite-container'],
      {
        [styles['home-card__circular-container']]: !!onDelete,
        [styles['home-card__small-icon']]: !!onDelete
      }
    );

    const deleteContainerClasses = classNames(
      styles['home-card__delete-container'],
      styles['home-card__circular-container']
    );

    return (
      <Fragment>
        <HomecardLabelList renderLabels={renderLabels} />

        {handleFavourite && (
          <Favourite
            selected={favourite}
            onClick={handleFavouriteClick}
            className={favoriteContainerClasses}
            iconWithBorder
          />
        )}

        {!!onDelete && (
          <Trash className={deleteContainerClasses} onClick={onDelete} />
        )}

        <div ref={homecardRef} className={styles['home-card__image-container']}>
          {hasLazyLoad && !inView ? (
            <HomecardImagePlaceholder />
          ) : (
            renderImageContent()
          )}
        </div>
      </Fragment>
    );
  }
);

HomecardImage.propTypes = {
  favourite: PropTypes.bool,
  handleFavourite: PropTypes.func,
  isAnimatedFavorite: PropTypes.bool,
  onDelete: PropTypes.func,
  image: PropTypes.string.isRequired,
  imageSet: PropTypes.string,
  onLoad: PropTypes.func,
  rating: PropTypes.number,
  renderLabels: PropTypes.func,
  title: PropTypes.string.isRequired,
  discount: PropTypes.shape({
    quantity: PropTypes.string,
    expiresAt: PropTypes.string
  }),
  hasLazyLoad: PropTypes.bool,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string
    })
  ),
  onImageChange: PropTypes.func,
  lazyCarousel: PropTypes.bool,
  isShowStatus: PropTypes.bool
};

HomecardImage.defaultProps = {
  imageSet: '',
  favourite: false,
  onDelete: null,
  handleFavourite: null,
  isAnimatedFavorite: false,
  onLoad: () => {},
  rating: null,
  renderLabels: () => {},
  discount: null,
  hasLazyLoad: true,
  photos: [],
  onImageChange: onImageChangeDefault,
  lazyCarousel: false,
  isShowStatus: false
};

export default HomecardImage;
